import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { find, isNil, map, size } from "lodash";
import { message } from "components";
import { AICC_LANGUAGE_STATUS, AiccLanguageStatus } from "constants/aiccLanguage";
import { AiccAssetVersions, AiccTtsModelOption } from "types/model/aiccLearningAssets";
import { useAiccManagedLanguageNames } from "./useAiccManagedLanguageNames";
import { useLearningAssetsStore } from "../../useLearningAssetsStore";
import { confirmSaveLearningAsset } from "../confirmations";

interface RunWriteOptions {
  confirm?: (status: AiccLanguageStatus) => Promise<boolean>;
}

export type RunWrite<T> = (
  request: (languageId: number) => Promise<AiccAssetVersions<T>>,
  successMessage: string,
  onSuccess?: () => void,
  options?: RunWriteOptions
) => void;

const EMPTY_ITEMS: never[] = [];

const EMPTY_TTS_MODEL_OPTIONS: AiccTtsModelOption[] = [];

const EMPTY_VERSIONS = { versions: [] };

export function useLearningAssetVersions<T>(fetchVersions: (languageId: number) => Promise<AiccAssetVersions<T>>) {
  const { languages, areLanguagesFresh, isLanguagesLoading } = useAiccManagedLanguageNames();
  const [assetVersions, setAssetVersions] = useState<AiccAssetVersions<T>>(EMPTY_VERSIONS);
  const [isVersionsLoading, setIsVersionsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const languageId = useLearningAssetsStore((state) => state.languageId);
  const activeStatus = useLearningAssetsStore((state) => state.activeStatus);
  const setLanguageId = useLearningAssetsStore((state) => state.setLanguageId);
  const setActiveStatus = useLearningAssetsStore((state) => state.setActiveStatus);

  useEffect(() => {
    if (!areLanguagesFresh || !languages[0] || find(languages, { id: languageId })) {
      return;
    }
    setLanguageId(languages[0].id);
  }, [areLanguagesFresh, languageId, languages, setLanguageId]);

  const requestIdRef = useRef(0);

  const loadVersions = useCallback(
    (id: number) => {
      const requestId = ++requestIdRef.current;
      const isLatest = () => requestId === requestIdRef.current;
      setIsVersionsLoading(true);
      return fetchVersions(id)
        .then((response) => isLatest() && setAssetVersions(response))
        .catch(() => isLatest() && setAssetVersions(EMPTY_VERSIONS))
        .finally(() => isLatest() && setIsVersionsLoading(false));
    },
    [fetchVersions]
  );

  const isLanguageManaged = !isNil(languageId) && !!find(languages, { id: languageId });

  useEffect(() => {
    if (isNil(languageId) || !isLanguageManaged) {
      setAssetVersions(EMPTY_VERSIONS);
      return;
    }
    loadVersions(languageId);
  }, [isLanguageManaged, languageId, loadVersions]);

  const { versions, hasTransliteration, filters } = assetVersions;
  const selectedGroup = find(versions, ({ status }) => status === activeStatus) ?? versions[0];
  const groupStatus = selectedGroup?.status;
  const isEditable = !isNil(groupStatus) && groupStatus !== AICC_LANGUAGE_STATUS.public;
  const isDraft = groupStatus === AICC_LANGUAGE_STATUS.draft;

  const runWrite: RunWrite<T> = useCallback(
    (request, successMessage, onSuccess, options) => {
      if (isNil(languageId) || isNil(groupStatus)) {
        return;
      }
      const confirmWrite = options?.confirm ?? confirmSaveLearningAsset;
      confirmWrite(groupStatus).then((confirmed) => {
        if (!confirmed) {
          return;
        }
        setIsSaving(true);
        request(languageId)
          .then((response) => {
            message.success(successMessage);
            onSuccess?.();
            requestIdRef.current += 1;
            setAssetVersions(response);
          })
          .catch(() => undefined)
          .finally(() => setIsSaving(false));
      });
    },
    [groupStatus, languageId]
  );

  const languageOptions = useMemo(() => map(languages, ({ id, name }) => ({ label: name, value: id })), [languages]);

  const items = selectedGroup?.items ?? EMPTY_ITEMS;

  return {
    languageId,
    setLanguageId,
    languageOptions,
    versions,
    items,
    hasTransliteration: !!hasTransliteration,
    filters,
    ttsModel: selectedGroup?.ttsModel,
    ttsModelOptions: assetVersions.ttsModelOptions ?? EMPTY_TTS_MODEL_OPTIONS,
    groupStatus,
    setActiveStatus,
    isEditable,
    isDraft,
    canManage: isEditable && !isDraft,
    canDelete: size(items) > 1,
    isLoading: isLanguagesLoading || isVersionsLoading || isSaving,
    isSaving,
    runWrite,
  };
}
